'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ProductionStageForm, type ProductionStageFormRef } from '@/components/partials/production-stages'
import { useCreateProductionStage } from '@/hooks'
import { type CreateProductionStageParams } from '@/types/production-stage'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const productionStageFormRef = useRef<ProductionStageFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createProductionStage, isPending: isLoading } = useCreateProductionStage()

  const handleSubmit = (data: CreateProductionStageParams) => {
    createProductionStage(data, {
      onSuccess: async newProductionStage => {
        setIsRedirecting(true)
        queryClient.setQueryData(['production-stage', newProductionStage.id], newProductionStage)
        await queryClient.invalidateQueries({ queryKey: ['production-stages'] })
        toast.success(t('created_successfully'))
        router.push(`/production-stages/${newProductionStage.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateProductionStageParams) => {
    createProductionStage(data, {
      onSuccess: async newProductionStage => {
        queryClient.setQueryData(['production-stage', newProductionStage.id], newProductionStage)
        await queryClient.invalidateQueries({ queryKey: ['production-stages'] })
        const detailUrl = `/production-stages/${newProductionStage.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        productionStageFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (productionStageFormRef.current) {
        productionStageFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/production-stages')
  }

  return (
    <ProductionStageForm
      key={formKey}
      ref={productionStageFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
